import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { toast } from 'sonner';
import { X, Plus, Trash2, Save, Send, ShieldCheck, Camera, FileText } from 'lucide-react';
import { fmtRange, fmtDateTime, humanizeDates } from '@/lib/dates';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => (v === null || v === undefined || v === '' ? '—' : `$${Number(v).toFixed(2)}`);
const marginTxt = (v) => (v === null || v === undefined ? '—' : `${Number(v).toFixed(1)}%`);

const SRC_BADGE = {
  imported: 'bg-slate-100 text-slate-600',
  owner: 'bg-emerald-50 text-emerald-700',
  unconfirmed: 'bg-amber-50 text-amber-700',
};

const HistoricalBillingWorkspace = ({ workOrderId, onClose, onSaved }) => {
  const { token } = useContext(AuthContext);
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const [ws, setWs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [costs, setCosts] = useState([]);
  const [items, setItems] = useState([]);
  const [billing, setBilling] = useState('');
  const [notes, setNotes] = useState('');

  const hydrate = (d) => {
    setWs(d);
    setCosts((d.direct_costs || []).map((c) => ({ ...c })));
    setItems((d.billing_line_items || []).map((i) => ({ ...i })));
    setBilling(d.customer_billing_amount ?? '');
    setNotes(d.pricing_notes || '');
  };

  const load = () => {
    setLoading(true);
    axios.get(`${API}/work-orders/historical/${workOrderId}/workspace`, authHeaders)
      .then((r) => hydrate(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Could not load workspace'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (workOrderId) load(); /* eslint-disable-next-line */ }, [workOrderId]);

  // Live client-side preview of totals (server is authoritative on save)
  const importedMat = Number(ws?.imported_material_cost || 0);
  const directSum = costs.reduce((s, c) => s + Number(c.amount || 0), 0);
  const totalDirect = importedMat + directSum;
  const itemsSum = items.reduce((s, i) => s + Number(i.amount || 0), 0);
  const hasBilling = billing !== '' && billing !== null;
  const proposed = hasBilling || itemsSum ? Number(billing || 0) + itemsSum : null;
  const gp = proposed === null ? null : proposed - totalDirect;
  const margin = proposed && proposed !== 0 && gp !== null ? (gp / proposed) * 100 : null;

  const save = async (stage) => {
    setSaving(true);
    try {
      const body = {
        direct_costs: costs,
        billing_line_items: items.filter((i) => (i.description || '').trim()),
        customer_billing_amount: billing === '' ? '' : Number(billing),
        pricing_notes: notes,
      };
      if (stage) body.hist_billing_stage = stage;
      const r = await axios.patch(`${API}/work-orders/historical/${workOrderId}/workspace`, body, authHeaders);
      hydrate(r.data);
      toast.success(stage ? `Stage updated: ${stage}` : 'Billing workspace saved');
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const addCost = () => setCosts([...costs, { category: ws.cost_categories[0], description: '', amount: '', source: 'owner' }]);
  const addItem = () => setItems([...items, { description: '', amount: '' }]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" data-testid="hbw-overlay" onClick={onClose}>
      <div className="h-full w-full max-w-3xl overflow-y-auto bg-white shadow-2xl" data-testid="historical-billing-workspace" onClick={(e) => e.stopPropagation()}>
        {loading || !ws ? (
          <div className="p-8 text-slate-400">Loading workspace…</div>
        ) : (
          <div className="p-6">
            <div className="mb-4 flex items-start justify-between">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#8FC93A]">ROUT · Historical Billing Workspace</div>
                <h2 className="font-mono text-lg font-extrabold text-[#0E2A47]" data-testid="hbw-wo-number">{ws.tfs_work_order_number}</h2>
                <div className="text-sm text-slate-600">{ws.customer_name} · {ws.service_location}</div>
                <div className="text-xs text-slate-400" data-testid="hbw-location-id">Service Location ID: {ws.authoritative_service_location_id || '—'}</div>
              </div>
              <button onClick={onClose} data-testid="hbw-close" className="rounded-full p-2 text-slate-400 hover:bg-slate-100"><X size={18} /></button>
            </div>

            <div className="mb-4 flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-[#0E2A47] px-3 py-1 text-xs font-bold text-white" data-testid="hbw-stage">{ws.hist_billing_stage}</span>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">Billing status: {ws.billing_status || 'Not Submitted'}</span>
              <span className={`rounded-full px-3 py-1 text-xs font-semibold ${ws.docs_complete ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                <Camera size={11} className="mr-1 inline" />{ws.before_photos}B / {ws.after_photos}A · {ws.documentation_status}
              </span>
              {ws.photo_exception_status && <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">{ws.photo_exception_status}</span>}
            </div>

            {/* Job facts */}
            <div className="mb-4 grid grid-cols-2 gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm">
              <div><div className="text-xs text-slate-400">Service date</div><div className="font-medium">{fmtRange(ws.service_date, ws.service_date_end)}</div></div>
              <div><div className="text-xs text-slate-400">Service provider</div><div className="font-medium">{ws.service_provider || '—'}</div></div>
              <div className="col-span-2"><div className="text-xs text-slate-400">Work performed</div><div className="text-slate-700">{humanizeDates(ws.work_performed) || '—'}</div></div>
            </div>

            {/* Materials / supporting */}
            {(ws.materials?.length > 0 || ws.imported_material_cost != null) && (
              <div className="mb-4 rounded-xl border border-slate-200 p-3 text-sm" data-testid="hbw-materials">
                <div className="mb-1 flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-slate-500"><FileText size={12} /> Imported materials & receipts</div>
                <div className="text-slate-600">Imported material cost: <span className="font-semibold">{money(ws.imported_material_cost)}</span> <span className="ml-1 rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-500">imported</span></div>
                {(ws.materials || []).map((m, i) => (
                  <div key={i} className="mt-1 text-xs text-slate-500">{m.description || m.item} · {m.quantity ? `${m.quantity} × ` : ''}{money(m.unit_cost ?? m.total_cost)} {m.vendor ? `· ${m.vendor}` : ''}</div>
                ))}
              </div>
            )}

            {/* Direct costs */}
            <div className="mb-4">
              <div className="mb-1 flex items-center justify-between">
                <div className="text-xs font-bold uppercase tracking-wide text-slate-500">Direct work-order costs</div>
                <button onClick={addCost} data-testid="hbw-add-cost" className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50"><Plus size={12} /> Add cost</button>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-2">
                <div className="flex items-center justify-between px-1 py-1 text-xs text-slate-500">
                  <span>Imported material cost</span>
                  <span className="flex items-center gap-2"><span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px]">imported</span>{money(ws.imported_material_cost)}</span>
                </div>
                {costs.map((c, idx) => (
                  <div key={idx} className="mt-2 grid grid-cols-12 items-center gap-2" data-testid={`hbw-cost-${idx}`}>
                    <select value={c.category} data-testid={`hbw-cost-cat-${idx}`}
                      onChange={(e) => { const n = [...costs]; n[idx].category = e.target.value; setCosts(n); }}
                      className="col-span-3 rounded-md border border-slate-300 px-1 py-1 text-xs">
                      {ws.cost_categories.map((k) => <option key={k} value={k}>{k}</option>)}
                    </select>
                    <input value={c.description} placeholder="Description" data-testid={`hbw-cost-desc-${idx}`}
                      onChange={(e) => { const n = [...costs]; n[idx].description = e.target.value; setCosts(n); }}
                      className="col-span-4 rounded-md border border-slate-300 px-2 py-1 text-xs" />
                    <select value={c.source || 'owner'} data-testid={`hbw-cost-src-${idx}`}
                      onChange={(e) => { const n = [...costs]; n[idx].source = e.target.value; setCosts(n); }}
                      className={`col-span-2 rounded-md border border-slate-300 px-1 py-1 text-xs ${SRC_BADGE[c.source] || ''}`}>
                      <option value="owner">owner-entered</option>
                      <option value="unconfirmed">unconfirmed</option>
                    </select>
                    <div className="col-span-2 flex items-center gap-1"><span className="text-slate-400">$</span>
                      <input type="number" step="0.01" min="0" value={c.amount} data-testid={`hbw-cost-amt-${idx}`}
                        onChange={(e) => { const n = [...costs]; n[idx].amount = e.target.value; setCosts(n); }}
                        className="w-full rounded-md border border-slate-300 px-1 py-1 text-xs" />
                    </div>
                    <button onClick={() => setCosts(costs.filter((_, i) => i !== idx))} data-testid={`hbw-cost-del-${idx}`} className="col-span-1 text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            </div>

            {/* Customer billing */}
            <div className="mb-4">
              <div className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-500">Customer billing</div>
              <div className="rounded-xl border border-slate-200 p-3">
                <label className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Base customer billing amount</span>
                  <span className="flex items-center gap-1"><span className="text-slate-400">$</span>
                    <input type="number" step="0.01" min="0" value={billing} placeholder="blank" data-testid="hbw-billing-amount"
                      onChange={(e) => setBilling(e.target.value)} className="w-32 rounded-md border border-slate-300 px-2 py-1 text-sm" />
                  </span>
                </label>
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-xs text-slate-500">Additional billing line items</span>
                  <button onClick={addItem} data-testid="hbw-add-item" className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50"><Plus size={12} /> Add line</button>
                </div>
                {items.map((it, idx) => (
                  <div key={idx} className="mt-2 grid grid-cols-12 items-center gap-2" data-testid={`hbw-item-${idx}`}>
                    <input value={it.description} placeholder="Line description" data-testid={`hbw-item-desc-${idx}`}
                      onChange={(e) => { const n = [...items]; n[idx].description = e.target.value; setItems(n); }}
                      className="col-span-9 rounded-md border border-slate-300 px-2 py-1 text-xs" />
                    <div className="col-span-2 flex items-center gap-1"><span className="text-slate-400">$</span>
                      <input type="number" step="0.01" value={it.amount} data-testid={`hbw-item-amt-${idx}`}
                        onChange={(e) => { const n = [...items]; n[idx].amount = e.target.value; setItems(n); }}
                        className="w-full rounded-md border border-slate-300 px-1 py-1 text-xs" />
                    </div>
                    <button onClick={() => setItems(items.filter((_, i) => i !== idx))} data-testid={`hbw-item-del-${idx}`} className="col-span-1 text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                  </div>
                ))}
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="hbw-pricing-notes"
                  rows={2} placeholder="Pricing notes — how the customer price was established"
                  className="mt-3 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
              </div>
            </div>

            {/* Profitability summary */}
            <div className="mb-4 grid grid-cols-2 gap-3 rounded-xl border-2 border-[#0E2A47]/10 bg-[#0E2A47]/[0.03] p-4 sm:grid-cols-4" data-testid="hbw-summary">
              <div><div className="text-xs text-slate-400">Total proposed billing</div><div className="text-lg font-bold text-[#0E2A47]" data-testid="hbw-total-billing">{money(proposed)}</div></div>
              <div><div className="text-xs text-slate-400">Total direct cost</div><div className="text-lg font-bold text-[#0E2A47]" data-testid="hbw-total-cost">{money(totalDirect)}</div></div>
              <div><div className="text-xs text-slate-400">Projected gross profit</div><div className={`text-lg font-bold ${gp === null ? 'text-slate-400' : gp >= 0 ? 'text-emerald-600' : 'text-red-600'}`} data-testid="hbw-gross-profit">{money(gp)}</div></div>
              <div><div className="text-xs text-slate-400">Projected gross margin</div><div className="text-lg font-bold text-[#0E2A47]" data-testid="hbw-gross-margin">{marginTxt(margin)}</div></div>
            </div>

            {/* Workflow actions */}
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <button onClick={() => save(null)} disabled={saving} data-testid="hbw-save"
                className="inline-flex items-center gap-1 rounded-lg bg-[#5FA718] px-4 py-2 text-sm font-semibold text-white hover:bg-[#4e8c14] disabled:opacity-40"><Save size={14} /> Save</button>
              <select data-testid="hbw-stage-select" defaultValue="" onChange={(e) => { if (e.target.value) { save(e.target.value); e.target.value = ''; } }}
                className="rounded-lg border border-slate-300 px-2 py-2 text-sm">
                <option value="">Set stage…</option>
                {ws.stages.filter((s) => s !== 'Submitted to Billing Approvals' && s !== 'Billing Approved').map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <button onClick={() => save('Submitted to Billing Approvals')} disabled={saving || !ws.docs_complete || proposed === null}
                data-testid="hbw-submit-approval" title={!ws.docs_complete ? 'Blocked until photos or an approved exception are complete' : (proposed === null ? 'Enter a customer billing amount first' : '')}
                className="inline-flex items-center gap-1 rounded-lg bg-[#0E2A47] px-4 py-2 text-sm font-semibold text-white hover:bg-[#16324F] disabled:opacity-40"><Send size={14} /> Submit to Billing Approvals</button>
              <button onClick={() => save('Billing Approved')} disabled={saving || !ws.docs_complete}
                data-testid="hbw-approve" className="inline-flex items-center gap-1 rounded-lg border border-emerald-300 px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-40"><ShieldCheck size={14} /> Mark Billing Approved</button>
            </div>
            {!ws.docs_complete && <p className="mb-4 text-xs text-amber-600">Final billing approval is blocked until the required before/after photos (or an owner-approved photo exception) are complete. Pricing may be prepared now.</p>}

            {/* Audit history */}
            <div data-testid="hbw-audit">
              <div className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-500">Audit history</div>
              <div className="max-h-48 overflow-y-auto rounded-xl border border-slate-200 bg-white">
                {(ws.audit || []).slice().reverse().map((a, i) => (
                  <div key={i} className="border-b border-slate-100 px-3 py-1.5 text-xs last:border-0">
                    <span className="font-semibold text-[#0E2A47]">{a.action?.replace(/_/g, ' ')}</span>
                    {a.detail ? ` — ${a.detail}` : ''}{a.stage ? ` — ${a.stage}` : ''}
                    <span className="ml-1 text-slate-400">· {a.by_name || 'system'} · {fmtDateTime(a.at)}</span>
                  </div>
                ))}
                {!(ws.audit || []).length && <div className="px-3 py-3 text-xs text-slate-400">No changes recorded yet.</div>}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoricalBillingWorkspace;
